import React, { useState, useEffect, useCallback, useRef } from "react";
import "../styles/CarBoard.css";
import { ActionResult, Character, PlatformAreas } from "./types/ComponentTypes";
import { carrierRules } from "./carrierRules";
import { useGame } from "../GameContext";
import CharactersConfig from "./CharactersConfig";
import { CharacterActions } from "./CharacterActions";
import { LoadLogic } from "./GameLogic/LoadLogic";
const person = {
  animal: ["wolf", "goat", "cabbage"],
  family: ["mom", "dad", "boy2", "boy1"],
};

interface Boards {
  GameStyle: "animal" | "family";
}

const Board: React.FC<Boards> = ({ GameStyle }) => {
  const { gameMode, setMessage, currentAction, setGameMode, setIsMoving } =
    useGame();

  //подгрузка изображений
  const carrierPositionRef = useRef<"A" | "B" | "initial">("initial");
  //настоящая позиция катера/скутера
  const [isCarrierMirrored, setIsCarrierMirrored] = useState(false);
  // условие проверки нужно ли переворачивать (может убрать?)
  const [animatingCharacters, setAnimatingCharacters] = useState<Set<string>>(
    new Set(),
  );
  // состояние анимации
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  //хранение значение поля ввода
  const isGameLoseRef = useRef(false);
  //состояние поражения
  const isGameWonRef = useRef(false);
  //состояние победы
  const [programText, setProgramText] = useState("");
  //значение поля ввода для обработки
  const [currentLine, setCurrentLine] = useState<number>(-1);
  // индекс строки в процессе
  const [errorLine, setErrorLine] = useState<number>(-1);
  // индекс строки с ошибкой
  const [debugCurrentLine, setDebugCurrentLine] = useState<number>(0);
  // индекс строки дебаггера
  const executionRef = useRef(false);
  // состояние запущен ли Run (execute)
  const ErrorProcessingRef = useRef<ActionResult | null>(null);
  // ошибки во время процесса
  const abortControllerRef = useRef<AbortController | null>(null);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  useEffect(() => {
    setAllImagesLoaded(false);

    const images = [
      "/img/carrier/fon.svg",
      "/img/carrier/carrier.svg",
      "/img/carrier/carrier_family.svg",
      ...CharactersConfig[GameStyle].flatMap((c) => [
        c.svgUrl,
        c.svgUrlFail,
        c.svgIn,
      ]),
      ...(GameStyle === "family"
        ? ["/img/carrier/characters_family/boys_incarrier.svg"]
        : []),
    ];

    let loaded = 0;

    images.forEach((src) => {
      const img = new Image();
      img.src = src;

      img.onload = img.onerror = () => {
        loaded++;
        if (loaded === images.length) {
          setAllImagesLoaded(true);
        }
      };
    });
  }, [GameStyle]);
  // Инициализация персонажей
  const getInitialPlatforms = useCallback((): PlatformAreas => {
    return { A: CharactersConfig[GameStyle], B: [], Carrier: [] };
  }, [GameStyle]);

  const arrayPlatformRef = useRef(getInitialPlatforms());
  const checkAbort = (signal?: AbortSignal) => {
    if (signal?.aborted) {
      throw new Error("ABORTED");
    }
  };
  const delay = (ms: number, signal?: AbortSignal) =>
    new Promise<void>((res, rej) => {
      const id = setTimeout(res, ms);

      signal?.addEventListener("abort", () => {
        clearTimeout(id);
        rej(new Error("ABORTED"));
      });
    });
  const movingControl = (state: boolean) => {
    if (gameMode === "Control" || gameMode === "Debug") {
      setIsMoving(state);
    }
  };
  const startProgram = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };
  // Обработчик действий из TopBar
  useEffect(() => {
    switch (currentAction) {
      case "AB":
        if (gameMode === "Control") {
          moveCarrier("AB");
        } else {
          handleAddCommand("move(a,b)", 8);
        }
        break;

      case "BA":
        if (gameMode === "Control") {
          moveCarrier("BA");
        } else {
          handleAddCommand("move(b,a)", 8);
        }
        break;

      case "next":
        handleNext();
        break;

      case "restart":
        restartGame();
        break;
      case "stop":
        restartGame();
        setGameMode("Edit");
        break;

      default:
        if (currentAction && CharacterActions[GameStyle].has(currentAction)) {
          if (gameMode === "Control") {
            loadToCarrier(currentAction);
          } else {
            handleInsertCharacterToMove(`${currentAction}`);
          }
        }
        break;
    }
  }, [currentAction, gameMode, GameStyle]);

  // Анимация персонажей
  const animateCharacter = async (
    characterName: string,
    animationType: "fade-out" | "fade-in",
    signal?: AbortSignal,
  ): Promise<void> => {
    checkAbort(signal);

    return new Promise((resolve, reject) => {
      const animationKey = `${characterName}-${animationType}`;

      setAnimatingCharacters((prev) => new Set(prev).add(animationKey));

      const id = setTimeout(() => {
        setAnimatingCharacters((prev) => {
          const next = new Set(prev);
          next.delete(animationKey);
          return next;
        });
        resolve();
      }, 500);

      signal?.addEventListener("abort", () => {
        clearTimeout(id);
        reject(new Error("ABORTED"));
      });
    });
  };

  const setCursor = (pos: number) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    });
  };

  const handleAddCommand = useCallback(
    (command: string, cursorPosition?: number) => {
      if (gameMode !== "Edit" || isGameWonRef.current) return;

      setProgramText((prev) => {
        const newText = prev ? `${prev}\n${command}` : command;

        if (cursorPosition !== undefined) {
          const lastLineLength = command.length;
          const cursor = newText.length - lastLineLength + cursorPosition;

          setCursor(cursor);
        }

        return newText;
      });
    },
    [gameMode],
  );

  const parseMoveCommand = useCallback(
    (line: string) => {
      const match =
        GameStyle === "family"
          ? line.match(
              /^move\s*\(\s*([AB])\s*,\s*([AB])\s*(?:,\s*(mom|dad|boy1|boy2))?(?:\s*,\s*(boy1|boy2))?\s*\)$/i,
            )
          : line.match(
              /^move\s*\(\s*([AB])\s*,\s*([AB])\s*(?:,\s*(wolf|goat|cabbage))?\s*\)$/i,
            );

      if (!match) return null;

      return {
        fromPlatform: match[1].toUpperCase() as "A" | "B",
        toPlatform: match[2].toUpperCase() as "A" | "B",
        characters: [match[3], match[4]].filter(Boolean) as string[],
      };
    },
    [GameStyle],
  );

  const handleInsertCharacterToMove = useCallback(
    (character: string) => {
      if (gameMode !== "Edit" || isGameWonRef.current) return;
      const textarea = textareaRef.current;
      if (!textarea) return;
      const { selectionStart, value } = textarea;
      const lines = value.split("\n");
      const lineIndex = value.slice(0, selectionStart).split("\n").length - 1;
      const currentLine = lines[lineIndex];
      const normalizedLine = currentLine.replace(/,\s*\)$/, ")");
      const parsed = parseMoveCommand(normalizedLine);
      if (!parsed) return;
      const maxCharacters = GameStyle === "animal" ? 1 : 2;
      if (
        parsed.characters.length >= maxCharacters ||
        parsed.characters.includes(character)
      )
        return;
      const newCharacters = [...parsed.characters, character];
      const newLine = `move(${parsed.fromPlatform.toLowerCase()},${parsed.toPlatform.toLowerCase()}${
        newCharacters.length ? "," + newCharacters.join(",") : ""
      })`;
      lines[lineIndex] = newLine;
      const newText = lines.join("\n");
      setProgramText(newText);
      const cursorPos =
        value
          .slice(0, selectionStart)
          .split("\n")
          .slice(0, lineIndex)
          .join("\n").length +
        (lineIndex > 0 ? 1 : 0) +
        newLine.length -
        1;
      setCursor(cursorPos);
    },
    [gameMode, parseMoveCommand, GameStyle],
  );

  // Функции погрузки персонажа
  const loadToCarrier = async (
    characterName: string,
    signal?: AbortSignal,
  ): Promise<ActionResult> => {
    if (isGameLoseRef.current || isGameWonRef.current)
      return ActionResult.BLOCKED;
    movingControl(true);
    checkAbort(signal);
    const platforms = arrayPlatformRef.current;
    const platformKey = carrierPositionRef.current === "B" ? "B" : "A";

    const character = platforms[platformKey].find(
      (c) => c.name === characterName,
    );
    checkAbort(signal);
    if (!character) {
      setMessage(`На этом берегу нет ${characterName}.`);
      movingControl(false);
      return ActionResult.NOT_FOUND;
    }
    checkAbort(signal);
    const logic = LoadLogic[GameStyle];

    const {
      result,
      nextPlatforms,
      animatedCharacters = [],
    } = await logic.loadToCarrier({
      character,
      platforms,
      platformKey,
      setMessage,
    });
    checkAbort(signal);
    if (result !== ActionResult.OK || !nextPlatforms) {
      movingControl(false);
      return result;
    }
    checkAbort(signal);
    await Promise.all(
      animatedCharacters.map((name) =>
        animateCharacter(name, "fade-out", signal),
      ),
    );
    checkAbort(signal);
    arrayPlatformRef.current = nextPlatforms;
    checkAbort(signal);
    await Promise.all(
      animatedCharacters.map((name) =>
        animateCharacter(name, "fade-in", signal),
      ),
    );
    checkAbort(signal);
    movingControl(false);
    return ActionResult.OK;
  };

  // Выгрузка персонажей
  const unloadFromCarrier = async (
    targetPosition: "A" | "B",
    signal?: AbortSignal,
  ): Promise<ActionResult> => {
    if (isGameLoseRef.current || isGameWonRef.current)
      return ActionResult.BLOCKED;
    const currentPlatform = arrayPlatformRef.current;
    const carrierCharacters = [...currentPlatform.Carrier];

    if (carrierCharacters.length === 0) {
      if (GameStyle === "family") {
        return ActionResult.BLOCKED;
      }
      checkAbort(signal);
      setIsCarrierMirrored(targetPosition === "B");
      checkAbort(signal);
      await delay(500, signal);
      return ActionResult.OK;
    }
    checkAbort(signal);
    if (carrierCharacters.length > 1) {
      await animateCharacter("childrens", "fade-out", signal);
    } else {
      await animateCharacter(carrierCharacters[0].name, "fade-out", signal);
    }

    checkAbort(signal);

    const updatedPlatforms = {
      ...currentPlatform,
      Carrier: [],
      [targetPosition]: [
        ...currentPlatform[targetPosition],
        ...carrierCharacters,
      ],
    };

    arrayPlatformRef.current = updatedPlatforms;
    checkAbort(signal);

    const fadeInPromises = carrierCharacters.map((character) =>
      animateCharacter(character.name, "fade-in", signal),
    );
    await Promise.all(fadeInPromises);

    checkAbort(signal);
    setIsCarrierMirrored(targetPosition === "B");
    checkAbort(signal);

    // Единая задержка после всех анимаций
    await delay(300, signal);

    checkAbort(signal);
    if (checkWinCondition(arrayPlatformRef.current) && gameMode === "Control") {
      isGameWonRef.current = true;
      setMessage(
        "Ты  всех переправил, молодец! Теперь напиши программу.",
      );
    }
    return ActionResult.OK;
  };

  // Перемещение перевозчика и выгрузка в конце(unloadfromcarrier)
  const moveCarrier = async (
    direction: "AB" | "BA",
    signal?: AbortSignal,
  ): Promise<ActionResult> => {
    if (isGameLoseRef.current || isGameWonRef.current)
      return ActionResult.BLOCKED;
    movingControl(true);
    checkAbort(signal);
    if (
      GameStyle === "family" &&
      arrayPlatformRef.current.Carrier.length === 0
    ) {
      setMessage("Нельзя двигаться без водителя.");
      movingControl(false);
      return ActionResult.BLOCKED;
    }
    const positions = {
      AB: { from: "A", to: "B" },
      BA: { from: "B", to: "A" },
    } as const;

    const { from, to } = positions[direction];

    if (carrierPositionRef.current === to) {
      setMessage(`Водитель уже находится на берегу ${to}.`);
      movingControl(false);
      return ActionResult.BLOCKED;
    }
    checkAbort(signal);
    carrierPositionRef.current = to;
    checkAbort(signal);
    await delay(4000, signal);
    checkAbort(signal);
    if (GameStyle === "animal") {
      const stateRules = carrierRules(arrayPlatformRef.current[from]);

      if (stateRules !== "true") {
        isGameLoseRef.current = true;
        if (stateRules === "overGoat") {
          setMessage(
            gameMode === "Control"
              ? "Волк съел козу. Попробуй ещё раз"
              : "Волк съел козу.",
          );
        }
        if (stateRules === "overCabbage") {
          setMessage(
            gameMode === "Control"
              ? "Коза съела капусту. Попробуй ещё раз"
              : "Коза съела капусту.",
          );
        }
        movingControl(false);
        return ActionResult.GAME_OVER;
      }
    }
    checkAbort(signal);
    await unloadFromCarrier(to, signal);
    checkAbort(signal);
    movingControl(false);
    return ActionResult.OK;
  };

  const checkWinCondition = (platforms?: PlatformAreas): boolean => {
    const target = platforms || arrayPlatformRef.current;
    const total = GameStyle === "animal" ? 3 : 4;
    return target.B.length === total && target.A.length === 0;
  };

  const handleNext = useCallback(async () => {
    if (isGameLoseRef.current || isGameWonRef.current) return;

    if (!programText.trim()) {
      setMessage("Программа пуста.");
      return;
    }

    const lines = programText.split("\n").filter((line) => line.trim() !== "");

    if (debugCurrentLine >= lines.length) {
      return;
    }
    const lineIndex = debugCurrentLine;
    const line = lines[lineIndex];

    setCurrentLine(lineIndex);

    const moveMatch = line.match(
      /move\s*\(\s*([AB])\s*,\s*([AB])\s*(?:,\s*(wolf|goat|cabbage|mom|dad|boy1|boy2))?(?:\s*,\s*(wolf|goat|cabbage|mom|dad|boy1|boy2))?\s*\)/i,
    );

    if (!moveMatch) {
      setMessage(`Неверная команда: "${line}"`);
      setErrorLine(lineIndex);
      ErrorProcessingRef.current = ActionResult.INVALID_COMMAND;
      return;
    }

    const fromPlatform = moveMatch[1].toUpperCase() as "A" | "B";
    const toPlatform = moveMatch[2].toUpperCase() as "A" | "B";

    const characters = [moveMatch[3], moveMatch[4]]
      .filter(Boolean)
      .map((c) => c!.toLowerCase());
    if (
      GameStyle === "family" &&
      (characters.includes("mom") || characters.includes("dad")) &&
      (characters.includes("boy1") || characters.includes("boy2"))
    ) {
      setMessage("Скутер перегружен.");
      setCurrentLine(-1);
      setErrorLine(debugCurrentLine);
      ErrorProcessingRef.current = ActionResult.INVALID_COMMAND;
      return;
    }
     if (fromPlatform === toPlatform) {
      setMessage(`Записаны одинаковые платформы.`);
      setErrorLine(debugCurrentLine);
      ErrorProcessingRef.current = ActionResult.SAME_PLATFORM;
      return;
    }
    if (
      carrierPositionRef.current === toPlatform ||
      (carrierPositionRef.current === "initial" && toPlatform === "A")
    ) {
      const name = GameStyle === "animal" ? "Водитель" : "Скутер";
      setMessage(`${name} уже на берегу ${toPlatform}.`);
      setCurrentLine(-1);
      setErrorLine(debugCurrentLine);
      ErrorProcessingRef.current = ActionResult.INVALID_COMMAND;
      return;
    }

    for (const char of characters) {
      const loadResult = await loadToCarrier(char);

      if (loadResult !== ActionResult.OK) {
        setErrorLine(lineIndex);
        ErrorProcessingRef.current = loadResult;
        return;
      }

      if (isGameLoseRef.current || isGameWonRef.current) return;
    }

    const direction = `${fromPlatform}${toPlatform}` as "AB" | "BA";
    const moveResult = await moveCarrier(direction);

    if (moveResult !== ActionResult.OK) {
      setErrorLine(lineIndex);
      ErrorProcessingRef.current = moveResult;
      return;
    }

    if (isGameLoseRef.current) {
      setErrorLine(lineIndex);
      return;
    }
    const nextLine = debugCurrentLine + 1;
    setDebugCurrentLine(nextLine);
    const WinExetion = checkWinCondition(arrayPlatformRef.current);

    if (nextLine === lines.length) {
      if (WinExetion) {
        if (lines.length > 7 && GameStyle === "animal") {
          setMessage(
            "Ты всех переправил, молодец! Задачу можно решить за 7 ходов. Попробуй!",
          );
        }
        if (lines.length > 9 && GameStyle === "family") {
          setMessage(
            "Ты всех переправил, молодец! Задачу можно решить за 9 ходов. Попробуй!",
          );
        }
        setMessage("Ты всех переправил, молодец!");
      } else {
        setMessage("Отладка завершена. Все команды выполнены.");
      }
      setCurrentLine(-1);
    } else {
      setMessage(`Выполнена строка ${debugCurrentLine + 1}.`);
    }
  }, [programText, debugCurrentLine, gameMode]);

  //Построчное выполнение кода

  const executeProgram = useCallback(async () => {
    if (executionRef.current) return;
    executionRef.current = true;
    const signal = startProgram();

    try {
      if (!programText.trim()) {
        setMessage("Программы нет");
        ErrorProcessingRef.current = ActionResult.INVALID_COMMAND;
        executionRef.current = false;
        return;
      }

      const lines = programText
        .split("\n")
        .filter((line) => line.trim() !== "");

      for (let currentIndex = 0; currentIndex < lines.length; currentIndex++) {
        if (gameMode !== "Run") break;

        if (isGameLoseRef.current || isGameWonRef.current) break;
        setErrorLine(-1);
        setCurrentLine(currentIndex);
        const line = lines[currentIndex];
        const command = parseMoveCommand(line);

        if (!command) {
          setMessage(`Неверная команда: "${line}"`);
          setCurrentLine(-1);
          setErrorLine(currentIndex);
          ErrorProcessingRef.current = ActionResult.INVALID_COMMAND;
          return;
        }
        const { fromPlatform, toPlatform, characters } = command;
        if (fromPlatform === toPlatform) {
          setMessage(`Записаны одинаковые платформы`);
          setCurrentLine(-1);
          setErrorLine(currentIndex);
          executionRef.current = false;
          ErrorProcessingRef.current = ActionResult.SAME_PLATFORM;
          return;
        }

        if (
          carrierPositionRef.current === toPlatform ||
          (carrierPositionRef.current === "initial" && toPlatform === "A")
        ) {
          const name = GameStyle === "animal" ? "Водитель" : "Скутер";
          setMessage(`${name} уже на берегу ${toPlatform}`);
          setCurrentLine(-1);
          setErrorLine(currentIndex);
          executionRef.current = false;
          ErrorProcessingRef.current = ActionResult.INVALID_COMMAND;
          return;
        }

        const direction = `${fromPlatform}${toPlatform}` as "AB" | "BA";

        for (const char of characters) {
          const result = await loadToCarrier(char, signal);

          if (result !== ActionResult.OK) {
            setCurrentLine(-1);
            setErrorLine(currentIndex);
            ErrorProcessingRef.current = result;
            executionRef.current = false;
            return;
          }
        }

        const moveResult = await moveCarrier(direction, signal);

        if (moveResult !== ActionResult.OK) {
          setCurrentLine(-1);
          setErrorLine(currentIndex);
          ErrorProcessingRef.current = moveResult;
          executionRef.current = false;
          return;
        }
        await delay(1000, signal);
      }
      setCurrentLine(-1);
      const WinExetion = checkWinCondition(arrayPlatformRef.current);
      if (WinExetion) {
        if (lines.length > 7 && GameStyle === "animal") {
          setMessage(
            "Ты всех переправил, молодец! Задачу можно решить за 7 ходов. Попробуй!",
          );
        }
        if (lines.length > 9 && GameStyle === "family") {
          setMessage(
            "Ты всех переправил, молодец! Задачу можно решить за 9 ходов. Попробуй!",
          );
        }
        setMessage("Ты всех переправил, молодец!");
      } else {
        setMessage("Программа выполнена");
      }
    } catch (err: any) {
      if (err?.message === "ABORTED") {
        return;
      }
    } finally {
      setCurrentLine(-1);
      executionRef.current = false;
    }
  }, [gameMode, programText, setMessage, GameStyle]);

  // Сброс при изменении режима игры
  const restartGame = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const initialPlatforms = getInitialPlatforms();
    arrayPlatformRef.current = initialPlatforms;
    carrierPositionRef.current = "initial";
    setIsCarrierMirrored(false);
    isGameLoseRef.current = false;
    isGameWonRef.current = false;
    ErrorProcessingRef.current = ActionResult.OK;
    executionRef.current = false;
    setCurrentLine(-1);
    setErrorLine(-1);
    setDebugCurrentLine(0);
    setAnimatingCharacters(new Set());
    setIsMoving(false);
    setMessage("");
  }, [getInitialPlatforms, setMessage]);

  useEffect(() => {
    restartGame();

    if (gameMode === "Run") {
      executeProgram();
    }
  }, [gameMode, restartGame, executeProgram]);

  // Рендер консоли программы
  const renderProgram = useCallback(() => {
    if (programText.trim() === "") {
      return <div className="car-program-empty">Программа пуста</div>;
    }

    const lines = programText.split("\n").filter((line) => line.trim() !== "");

    return lines.map((line, index) => {
      let className = "car-program-line";
      if (index === currentLine) className += " current-line";
      if (index === errorLine) className += " error-line";

      return (
        <div key={index} className={className}>
          {line}
        </div>
      );
    });
  }, [programText, currentLine, errorLine]);

  const renderCharacters = (
    characters: Character[],
    characterName: string,
    platform: "A" | "B",
  ) => {
    const character = characters.find((char) => char.name === characterName);
    if (!character) return null;

    const isFadingOut = animatingCharacters.has(`${characterName}-fade-out`);
    const isFadingIn = animatingCharacters.has(`${characterName}-fade-in`);

    let imageUrl = character.svgUrl;
    let isFailedCharacter = false;

    if (isGameLoseRef.current && carrierRules(characters) !== "true") {
      const hasWolf = characters.some((char) => char.name === "wolf");
      const hasGoat = characters.some((char) => char.name === "goat");
      const hasCabbage = characters.some((char) => char.name === "cabbage");

      if (hasWolf && hasGoat && character.name === "goat") {
        imageUrl = character.svgUrlFail;
        isFailedCharacter = true;
      } else if (hasGoat && hasCabbage && character.name === "cabbage") {
        imageUrl = character.svgUrlFail;
        isFailedCharacter = true;
      }
    }

    return (
      <img
        key={character.id}
        src={imageUrl}
        alt={character.id}
        className={`car-character ${
          platform === "B" && GameStyle === "family"
            ? `car-person-${character.name}B`
            : `car-person-${character.name}`
        } ${isFadingOut ? "fade-out" : isFadingIn ? "fade-in" : ""} ${
          isFailedCharacter ? "car-character-failed" : ""
        }`}
      />
    );
  };

  const renderCarrier = () => {
    let carrierClass = "";
    const positionCar = carrierPositionRef.current;
    if (positionCar === "A") {
      carrierClass = "carrier-left";
    } else if (positionCar === "B") {
      carrierClass = "carrier-right";
    } else if (positionCar === "initial") {
      carrierClass = "carrier-no-animation";
    }

    let character: Character | undefined;

    if (arrayPlatformRef.current.Carrier.length > 1) {
      character = {
        id: "character-childrens",
        name: "childrens",
        size: 5,
        svgUrl: "/img/carrier/characters_family/boys_incarrier.svg",
        svgUrlFail: "",
        svgIn: "/img/carrier/characters_family/boys_incarrier.svg",
        type: "child",
      };
    } else {
      character = arrayPlatformRef.current.Carrier[0];
    }

    const isFadingOut =
      character && animatingCharacters.has(`${character.name}-fade-out`);
    const isFadingIn =
      character && animatingCharacters.has(`${character.name}-fade-in`);

    return (
      <div
        className={`car-platform ${carrierClass} ${
          GameStyle === "family" ? "carrier-family" : "carrier-animal"
        } ${isCarrierMirrored ? "carrier-mirrored" : ""}`.trim()}
      >
        {GameStyle === "animal" && character && (
          <img
            key={character.id}
            src={character.svgIn}
            alt={character.id}
            className={`car-character incarrier-${character.name} ${
              isFadingOut ? "fade-out" : isFadingIn ? "fade-in" : ""
            }`}
            style={{ position: "absolute", zIndex: 1 }}
          />
        )}

        <img
          src={
            GameStyle === "family"
              ? "/img/carrier/carrier_family.svg"
              : "/img/carrier/carrier.svg"
          }
          className={
            GameStyle === "animal" ? "person-carrier" : "person-carrier-family"
          }
          alt="carrier"
          style={{ position: "relative", zIndex: 2 }}
        />

        {GameStyle === "family" && character && (
          <img
            key={character.id}
            src={character.svgIn}
            alt={character.id}
            className={`car-character incarrier-${character.name} ${
              isFadingOut ? "fade-out" : isFadingIn ? "fade-in" : ""
            }`}
            style={{ position: "absolute", zIndex: 3 }}
          />
        )}
      </div>
    );
  };

  const orderForB =
    GameStyle === "animal"
      ? ["goat", "wolf", "cabbage"]
      : ["mom", "dad", "boy2", "boy1"];

  return (
    <div className="car-Board">
      <div className="car-Scene">
        {!allImagesLoaded ? (
          <div className="board-loading">
            <div className="loading-text">Загрузка...</div>
          </div>
        ) : (
          <>
            <img
              className="car-BackgroundImg"
              src={`/img/carrier/fon.svg`}
              alt="Фон"
            />
            <div className={`car-platform car-A-${GameStyle}`}>
              {person[GameStyle].map((item) => (
                <div key={item} id={`${item}A`}>
                  {renderCharacters(arrayPlatformRef.current.A, item, "A")}
                </div>
              ))}
            </div>
            <div className={`car-platform car-B-${GameStyle}`}>
              {orderForB.map((item) => (
                <div key={item} id={`${item}B`}>
                  {renderCharacters(arrayPlatformRef.current.B, item, "B")}
                </div>
              ))}
            </div>
            {renderCarrier()}
          </>
        )}
      </div>

      <div className="car-Management-bar">
        {gameMode === "Run" || gameMode === "Debug" ? (
          <div className="car-console run">{renderProgram()}</div>
        ) : (
          <textarea
            className="car-console edit"
            ref={textareaRef}
            value={programText}
            onChange={(e) => setProgramText(e.target.value)}
            style={{
              background: gameMode === "Edit" ? "rgb(227, 241, 250)" : "white",
              pointerEvents: gameMode !== "Edit" ? "none" : "auto",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Board;
